import { useMemo, useEffect } from 'react';
import { Check, HelpCircle, User, Calendar, Info } from 'lucide-react';
import { Label } from '~/components/ui/label';
import { Checkbox } from '~/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { calculateMonthlyPaymentCount } from '~/components/PaymentSchedulePreview';

interface StudentInfo {
  id: string;
  firstName: string;
  lastName: string;
  type: 'returning' | 'new';
}

interface TuitionChunkProps {
  enrollingStudents: StudentInfo[];
  tuitionOptions: any[];
  paymentOptions: any[];
  tuitionPreferences: any;
  onUpdate: (tuitionPreferences: any) => void;
  schoolYearStartDate?: string | null;
  schoolYearEndDate?: string | null;
}

/**
 * Reusable tuition selection chunk.
 * Used by both reenrollment TuitionStep and application AppTuitionStep.
 */
export function TuitionChunk({
  enrollingStudents,
  tuitionOptions,
  paymentOptions,
  tuitionPreferences,
  onUpdate,
  schoolYearStartDate,
  schoolYearEndDate,
}: TuitionChunkProps) {
  // Auto-select tuition option if only one exists
  useEffect(() => {
    if (tuitionOptions.length === 1 && enrollingStudents.length > 0) {
      const singleOption = tuitionOptions[0];
      const studentSelections = tuitionPreferences.studentSelections || {};

      const needsAutoSelect = enrollingStudents.some(
        (student) => !studentSelections[student.id]?.tuitionOptionKey
      );

      if (needsAutoSelect) {
        const newSelections = { ...studentSelections };
        enrollingStudents.forEach((student) => {
          if (!newSelections[student.id]?.tuitionOptionKey) {
            newSelections[student.id] = {
              ...newSelections[student.id],
              tuitionOptionKey: singleOption.key
            };
          }
        });

        onUpdate({
          ...tuitionPreferences,
          studentSelections: newSelections
        });
      }
    }
  }, [tuitionOptions, enrollingStudents, tuitionPreferences, onUpdate]);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const studentSelections = useMemo(() => tuitionPreferences.studentSelections || {}, [tuitionPreferences.studentSelections]);
  const selectedPaymentOptionKey = tuitionPreferences.paymentOptionKey || '';

  const handleStudentTuitionChange = (studentId: string, tuitionKey: string) => {
    const newSelections = {
      ...studentSelections,
      [studentId]: {
        ...studentSelections[studentId],
        tuitionOptionKey: tuitionKey
      }
    };

    onUpdate({
      ...tuitionPreferences,
      studentSelections: newSelections
    });
  };

  const handleFamilyPaymentChange = (paymentKey: string) => {
    onUpdate({
      ...tuitionPreferences,
      paymentOptionKey: paymentKey
    });
  };

  const handleAssistanceChange = (checked: boolean) => {
    onUpdate({
      ...tuitionPreferences,
      seekingFinancialAssistance: checked
    });
  };

  const selectedTuitionKeys = useMemo(() => {
    return [...new Set(
      Object.values(studentSelections)
        .map((s: any) => s?.tuitionOptionKey)
        .filter(Boolean)
    )];
  }, [studentSelections]);

  const availablePaymentOptions = useMemo(() => {
    if (selectedTuitionKeys.length === 0) return [];

    return paymentOptions.filter((po: any) => {
      if (!po.appliesTo || po.appliesTo.length === 0) return true;
      return selectedTuitionKeys.every((key: string) => po.appliesTo.includes(key));
    });
  }, [paymentOptions, selectedTuitionKeys]);

  const calculateTotals = () => {
    let totalTuition = 0;
    let totalDiscount = 0;

    enrollingStudents.forEach((student) => {
      const selection = studentSelections[student.id];
      if (selection?.tuitionOptionKey) {
        const tuitionOpt = tuitionOptions.find((t: any) => t.key === selection.tuitionOptionKey);
        if (tuitionOpt) {
          totalTuition += tuitionOpt.costCents || 0;
        }
      }
    });

    if (selectedPaymentOptionKey) {
      const paymentOpt = paymentOptions.find((p: any) => p.key === selectedPaymentOptionKey);
      if (paymentOpt?.discountCents) {
        if (paymentOpt.discountType === 'flat') {
          totalDiscount = paymentOpt.discountCents;
        } else {
          const studentsWithTuition = enrollingStudents.filter(
            (student) => studentSelections[student.id]?.tuitionOptionKey
          ).length;
          totalDiscount = paymentOpt.discountCents * studentsWithTuition;
        }
      }
    }

    return {
      totalTuition,
      totalDiscount,
      netTotal: Math.max(totalTuition - totalDiscount, 0)
    };
  };

  const totals = calculateTotals();
  const selectedPaymentOption = paymentOptions.find((p: any) => p.key === selectedPaymentOptionKey);

  const paymentSchedule = useMemo(() => {
    if (!selectedPaymentOption || totals.netTotal <= 0) return [];

    const scheduleType = selectedPaymentOption.scheduleType;
    const billingDay = selectedPaymentOption.billingDayOfMonth || 1;
    const startMonth = selectedPaymentOption.startMonth || 9;

    let numPayments;
    switch (scheduleType) {
      case 'annual':
        numPayments = 1;
        break;
      case 'biannual':
        numPayments = 2;
        break;
      case 'monthly':
      default:
        numPayments = calculateMonthlyPaymentCount(schoolYearStartDate, schoolYearEndDate);
        break;
    }

    const amountPerPayment = Math.floor(totals.netTotal / numPayments);
    const remainder = totals.netTotal % numPayments;

    const schedule: any[] = [];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];

    let currentMonth = startMonth - 1;
    const currentYear = new Date().getFullYear();

    for (let i = 0; i < numPayments; i++) {
      const year = currentMonth < (startMonth - 1) ? currentYear + 1 : currentYear;
      const monthName = monthNames[currentMonth % 12];
      const amount = amountPerPayment + (i < remainder ? 1 : 0);

      schedule.push({
        date: `${monthName} ${billingDay}, ${year}`,
        amount
      });

      if (scheduleType === 'biannual') {
        currentMonth += 6;
      } else {
        currentMonth += 1;
      }
    }

    return schedule;
  }, [selectedPaymentOption, totals.netTotal, schoolYearStartDate, schoolYearEndDate]);

  const allStudentsHaveTuition = enrollingStudents.every(
    (student) => studentSelections[student.id]?.tuitionOptionKey
  );

  const hasSingleTuitionOption = tuitionOptions.length === 1;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {enrollingStudents.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <HelpCircle className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="text-muted-foreground">
              No students are currently selected for enrollment. Please go back and select students.
            </p>
          </CardContent>
        </Card>
      ) : tuitionOptions.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <HelpCircle className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="text-muted-foreground">
              Tuition options have not been configured yet. The school will provide payment information separately.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Per-student tuition selection */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Tuition per Student</Label>

            {hasSingleTuitionOption && (
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md text-sm">
                <Info className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">
                  One tuition option is available: <span className="font-medium">{tuitionOptions[0].name}</span> ({formatCurrency(tuitionOptions[0].costCents)})
                </span>
              </div>
            )}

            {enrollingStudents.map((student) => {
              const selection = studentSelections[student.id] || {};
              const selectedTuition = tuitionOptions.find((t: any) => t.key === selection.tuitionOptionKey);

              return (
                <Card key={student.id}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <User className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <span className="font-medium">
                            {student.firstName} {student.lastName}
                          </span>
                          {student.type === 'new' && (
                            <Badge variant="secondary" className="ml-2">New</Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {hasSingleTuitionOption ? (
                          <div className="flex items-center gap-2 text-sm">
                            <Check className="h-4 w-4 text-green-600" />
                            <span>{selectedTuition?.name}</span>
                            <span className="font-medium">{formatCurrency(selectedTuition?.costCents || 0)}</span>
                          </div>
                        ) : (
                          <Select
                            value={selection.tuitionOptionKey || ''}
                            onValueChange={(val) => handleStudentTuitionChange(student.id, val)}
                          >
                            <SelectTrigger className="w-[280px]">
                              <SelectValue placeholder="Select tuition option" />
                            </SelectTrigger>
                            <SelectContent>
                              {tuitionOptions
                                .filter((opt: any) => opt && opt.key)
                                .map((opt: any) => (
                                  <SelectItem key={opt.key} value={opt.key}>
                                    {opt.name} - {formatCurrency(opt.costCents || 0)}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>

                    {selectedTuition?.description && (
                      <p className="text-xs text-muted-foreground mt-2 ml-8">{selectedTuition.description}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Family-level Payment Plan Selection */}
          {allStudentsHaveTuition && (
            <div className="space-y-4">
              <Label className="text-base font-medium">Family Payment Plan</Label>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Select Payment Schedule
                  </CardTitle>
                  <CardDescription>
                    Choose how you would like to pay for all students
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Select
                    value={selectedPaymentOptionKey}
                    onValueChange={handleFamilyPaymentChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a payment plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePaymentOptions.map((opt: any) => (
                        <SelectItem key={opt.key} value={opt.key}>
                          <div className="flex items-center justify-between w-full">
                            <span>{opt.name}</span>
                            {opt.discountCents > 0 && (
                              <Badge variant="outline" className="ml-2 text-green-600 border-green-600">
                                {formatCurrency(opt.discountCents)} {opt.discountType === 'per_student' ? '/student' : ''} off
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {selectedPaymentOption?.description && (
                    <p className="text-sm text-muted-foreground">{selectedPaymentOption.description}</p>
                  )}

                  {/* Payment Schedule Preview */}
                  {paymentSchedule.length > 0 && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Payment Schedule Preview
                      </h4>
                      <div className="space-y-2">
                        {paymentSchedule.map((payment: any, idx: number) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{payment.date}</span>
                            <span className="font-medium">{formatCurrency(payment.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Total Summary */}
          {totals.totalTuition > 0 && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="py-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Total Tuition:</span>
                    <span>{formatCurrency(totals.totalTuition)}</span>
                  </div>
                  {totals.totalDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount:</span>
                      <span>-{formatCurrency(totals.totalDiscount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="font-semibold">Annual Total:</span>
                    <span className="text-2xl font-bold">{formatCurrency(totals.netTotal)}</span>
                  </div>
                  {selectedPaymentOption && paymentSchedule.length > 1 && (
                    <div className="text-sm text-muted-foreground text-right">
                      {paymentSchedule.length} payments of ~{formatCurrency(Math.floor(totals.netTotal / paymentSchedule.length))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Financial Assistance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Financial Assistance</CardTitle>
          <CardDescription>
            Our school offers financial assistance to families who qualify. Assistance is provided based on demonstrated need and available funds.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start space-x-3">
            <Checkbox
              id="financial-assistance"
              checked={tuitionPreferences.seekingFinancialAssistance || false}
              onCheckedChange={handleAssistanceChange}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="financial-assistance"
                className="cursor-pointer font-medium"
              >
                We are seeking financial assistance
              </Label>
              <p className="text-sm text-muted-foreground">
                By checking this box, you indicate interest in learning more about financial assistance options. A member of our team will follow up with you.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
